import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ADMIN_WORKFLOW_KEYS,
  AddProposalItemPayload,
  ConvertToContractPayload,
  CreateProposalPayload,
  CreateProposalFromOpportunityPayload,
  CreateWorkflowPayload,
  CreateWorkflowStepPayload,
  DecideApprovalPayload,
  DispatchProposalPayload,
  ListPropostasParams,
  PROPOSAL_KEYS,
  RecordAcceptancePayload,
  RecordNegotiationPayload,
  RecordRejectionPayload,
  RequestSignaturePayload,
  UpdateProposalItemPayload,
  UpdateProposalPayload,
  UpdateWorkflowPayload,
  UpdateWorkflowStepPayload,
  UpsertThresholdPayload,
  addItem,
  addRecipient,
  addWorkflowStep,
  convertToContract,
  createFromOpportunity,
  createProposal,
  createWorkflow,
  deleteWorkflow,
  getWorkflow,
  decideApproval,
  deleteProposal,
  dispatchProposal,
  duplicateProposal,
  getProposal,
  getVersionDiff,
  listApprovals,
  listAttachments,
  listEligibleContacts,
  listItems,
  listProposals,
  listRecipientsForVersion,
  listThresholds,
  listWorkflows,
  recordAcceptance,
  recordNegotiation,
  recordRejection,
  removeAttachment,
  removeItem,
  removeRecipient,
  removeWorkflowStep,
  requestApproval,
  requestSignature,
  seedItemsFromCommercialBase,
  updateItem,
  updateProposal,
  updateWorkflow,
  updateWorkflowStep,
  uploadAttachment,
  upsertThreshold,
} from '../api';

/**
 * Hooks TanStack Query do módulo Propostas Comerciais (US-COM-006).
 *
 * **Chave de invalidação.** `PROPOSAL_KEYS.all` reset da árvore inteira
 * é o mais seguro em operações que criam versão (contraproposta,
 * transição para `enviada_ao_cliente` que estabiliza a versão vigente).
 * Para escritas locais (item, anexo, recipient), invalidamos só o
 * subtree afetado + o detalhe (o backend recalcula totais).
 */

export { PROPOSAL_KEYS };

// ---- Leitura --------------------------------------------------------

export function usePropostas(params?: ListPropostasParams) {
  return useQuery({
    queryKey: PROPOSAL_KEYS.list(params),
    queryFn: () => listProposals(params),
  });
}

export function useProposta(id: string | undefined) {
  return useQuery({
    queryKey: id ? PROPOSAL_KEYS.detail(id) : ['propostas', 'detail', 'noop'],
    queryFn: () => getProposal(id as string),
    enabled: !!id,
  });
}

export function useItensDaVersao(proposalId: string | undefined, versionId: string | undefined) {
  return useQuery({
    queryKey:
      proposalId && versionId
        ? PROPOSAL_KEYS.items(versionId)
        : ['propostas', 'items', 'noop'],
    queryFn: () => listItems(proposalId as string, versionId as string),
    enabled: !!proposalId && !!versionId,
  });
}

export function useAprovacoesDaVersao(versionId: string | undefined) {
  return useQuery({
    queryKey: versionId ? PROPOSAL_KEYS.approvals(versionId) : ['propostas', 'approvals', 'noop'],
    queryFn: () => listApprovals(versionId as string),
    enabled: !!versionId,
  });
}

export function useAnexosDaProposta(id: string | undefined, versionId?: string) {
  return useQuery({
    queryKey: id
      ? PROPOSAL_KEYS.attachments(id, versionId)
      : ['propostas', 'attachments', 'noop'],
    queryFn: () => listAttachments(id as string, versionId),
    enabled: !!id,
  });
}

export function useContatosElegiveis(id: string | undefined) {
  return useQuery({
    queryKey: id ? PROPOSAL_KEYS.eligibleContacts(id) : ['propostas', 'eligible', 'noop'],
    queryFn: () => listEligibleContacts(id as string),
    enabled: !!id,
    /**
     * Contatos ativos mudam pouco durante o fluxo de envio — 60s de
     * cache reduz retrabalho no wizard. Se um contato foi substituído
     * no meio, o backend rejeita a operação de add/dispatch mesmo
     * assim (fonte de verdade continua no server).
     */
    staleTime: 60 * 1000,
  });
}

export function useDestinatariosDaVersao(
  proposalId: string | undefined,
  versionId: string | undefined,
) {
  return useQuery({
    queryKey:
      proposalId && versionId
        ? PROPOSAL_KEYS.recipients(versionId)
        : ['propostas', 'recipients', 'noop'],
    queryFn: () => listRecipientsForVersion(proposalId as string, versionId as string),
    enabled: !!proposalId && !!versionId,
  });
}

export function useVersionDiff(
  proposalId: string | undefined,
  versionAId: string | undefined,
  versionBId: string | undefined,
) {
  return useQuery({
    queryKey:
      proposalId && versionAId && versionBId
        ? PROPOSAL_KEYS.diff(proposalId, versionAId, versionBId)
        : ['propostas', 'diff', 'noop'],
    queryFn: () =>
      getVersionDiff(proposalId as string, versionAId as string, versionBId as string),
    enabled: !!proposalId && !!versionAId && !!versionBId,
  });
}

// ---- Mutations helpers ----------------------------------------------

function useInvalidarProposta() {
  const qc = useQueryClient();
  return (id?: string) => {
    void qc.invalidateQueries({ queryKey: PROPOSAL_KEYS.all });
    if (id) {
      void qc.invalidateQueries({ queryKey: PROPOSAL_KEYS.detail(id) });
    }
  };
}

// ---- Cadastro / edição ----------------------------------------------

export function useCriarProposta() {
  const invalidar = useInvalidarProposta();
  return useMutation({
    mutationFn: (payload: CreateProposalPayload) => createProposal(payload),
    onSuccess: (p) => invalidar(p.id),
  });
}

export function useCriarPropostaDeOportunidade() {
  const invalidar = useInvalidarProposta();
  return useMutation({
    mutationFn: (input: {
      opportunityId: string;
      payload: CreateProposalFromOpportunityPayload;
    }) => createFromOpportunity(input.opportunityId, input.payload),
    onSuccess: (p) => invalidar(p.id),
  });
}

export function useAtualizarProposta() {
  const invalidar = useInvalidarProposta();
  return useMutation({
    mutationFn: (input: { id: string; payload: UpdateProposalPayload }) =>
      updateProposal(input.id, input.payload),
    onSuccess: (p) => invalidar(p.id),
  });
}

export function useRemoverProposta() {
  const invalidar = useInvalidarProposta();
  return useMutation({
    mutationFn: (id: string) => deleteProposal(id),
    onSuccess: () => invalidar(),
  });
}

export function useDuplicarProposta() {
  const invalidar = useInvalidarProposta();
  return useMutation({
    mutationFn: (id: string) => duplicateProposal(id),
    onSuccess: (p) => invalidar(p.id),
  });
}

// ---- Itens ----------------------------------------------------------

export function useAdicionarItem(proposalId: string, versionId: string) {
  const invalidar = useInvalidarProposta();
  return useMutation({
    mutationFn: (payload: AddProposalItemPayload) => addItem(proposalId, versionId, payload),
    onSuccess: () => invalidar(proposalId),
  });
}

export function useAtualizarItem(proposalId: string) {
  const invalidar = useInvalidarProposta();
  return useMutation({
    mutationFn: (input: { itemId: string; payload: UpdateProposalItemPayload }) =>
      updateItem(proposalId, input.itemId, input.payload),
    onSuccess: () => invalidar(proposalId),
  });
}

export function useRemoverItem(proposalId: string) {
  const invalidar = useInvalidarProposta();
  return useMutation({
    mutationFn: (itemId: string) => removeItem(proposalId, itemId),
    onSuccess: () => invalidar(proposalId),
  });
}

export function useSemearItensDaBase(proposalId: string, versionId: string) {
  const invalidar = useInvalidarProposta();
  return useMutation({
    mutationFn: () => seedItemsFromCommercialBase(proposalId, versionId),
    onSuccess: () => invalidar(proposalId),
  });
}

// ---- Anexos ---------------------------------------------------------

export function useEnviarAnexo(proposalId: string, versionId: string) {
  const invalidar = useInvalidarProposta();
  return useMutation({
    mutationFn: (input: { file: File; descricao?: string }) =>
      uploadAttachment(proposalId, versionId, input.file, input.descricao),
    onSuccess: () => invalidar(proposalId),
  });
}

export function useRemoverAnexo(proposalId: string, versionId: string) {
  const invalidar = useInvalidarProposta();
  return useMutation({
    mutationFn: (attachmentId: string) => removeAttachment(proposalId, versionId, attachmentId),
    onSuccess: () => invalidar(proposalId),
  });
}

// ---- Destinatários --------------------------------------------------

export function useAdicionarDestinatario(proposalId: string, versionId: string) {
  const invalidar = useInvalidarProposta();
  return useMutation({
    mutationFn: (payload: { contactId: string; isPrimary?: boolean }) =>
      addRecipient(proposalId, versionId, payload),
    onSuccess: () => invalidar(proposalId),
  });
}

export function useRemoverDestinatario(proposalId: string, versionId: string) {
  const invalidar = useInvalidarProposta();
  return useMutation({
    mutationFn: (recipientId: string) => removeRecipient(proposalId, versionId, recipientId),
    onSuccess: () => invalidar(proposalId),
  });
}

// ---- Ciclo de vida --------------------------------------------------

export function useSolicitarAprovacao(proposalId: string, versionId: string) {
  const invalidar = useInvalidarProposta();
  return useMutation({
    mutationFn: (input?: { workflowId?: string }) =>
      requestApproval(proposalId, versionId, input?.workflowId),
    onSuccess: () => invalidar(proposalId),
  });
}

export function useDecidirAprovacao() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { approvalId: string; payload: DecideApprovalPayload }) =>
      decideApproval(input.approvalId, input.payload),
    /**
     * Aprovação pode transitar a versão para `aprovada_internamente` no
     * último step — reset amplo evita cache stale de listagens filtradas
     * por status.
     */
    onSuccess: () => qc.invalidateQueries({ queryKey: PROPOSAL_KEYS.all }),
  });
}

export function useEnviarProposta(proposalId: string, versionId: string) {
  const invalidar = useInvalidarProposta();
  return useMutation({
    mutationFn: (input: { file: File; payload: DispatchProposalPayload }) =>
      dispatchProposal(proposalId, versionId, input.file, input.payload),
    onSuccess: () => invalidar(proposalId),
  });
}

export function useRegistrarNegociacao(proposalId: string, versionId: string) {
  const invalidar = useInvalidarProposta();
  return useMutation({
    mutationFn: (payload: RecordNegotiationPayload) =>
      recordNegotiation(proposalId, versionId, payload),
    /**
     * Contraproposta gera nova versão — invalida tudo (a versão vigente
     * muda e o listing pega o novo status).
     */
    onSuccess: () => invalidar(proposalId),
  });
}

export function useRegistrarAceite(proposalId: string, versionId: string) {
  const invalidar = useInvalidarProposta();
  return useMutation({
    mutationFn: (payload: RecordAcceptancePayload) =>
      recordAcceptance(proposalId, versionId, payload),
    onSuccess: () => invalidar(proposalId),
  });
}

export function useRegistrarRejeicao(proposalId: string, versionId: string) {
  const invalidar = useInvalidarProposta();
  return useMutation({
    mutationFn: (payload: RecordRejectionPayload) =>
      recordRejection(proposalId, versionId, payload),
    onSuccess: () => invalidar(proposalId),
  });
}

export function useConverterEmContrato(proposalId: string, versionId: string) {
  const invalidar = useInvalidarProposta();
  return useMutation({
    mutationFn: (payload: ConvertToContractPayload) =>
      convertToContract(proposalId, versionId, payload),
    onSuccess: () => invalidar(proposalId),
  });
}

export function useSolicitarAssinatura(proposalId: string, versionId: string) {
  const invalidar = useInvalidarProposta();
  return useMutation({
    mutationFn: (payload: RequestSignaturePayload) =>
      requestSignature(proposalId, versionId, payload),
    onSuccess: () => invalidar(proposalId),
  });
}

// ---- Admin de workflows (mini-fatia 2) ------------------------------

export { ADMIN_WORKFLOW_KEYS };

function useInvalidarWorkflows() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: ADMIN_WORKFLOW_KEYS.all });
}

export function useWorkflows() {
  return useQuery({
    queryKey: ADMIN_WORKFLOW_KEYS.list(),
    queryFn: listWorkflows,
  });
}

export function useWorkflow(id: string | undefined) {
  return useQuery({
    queryKey: id ? ADMIN_WORKFLOW_KEYS.detail(id) : ['propostas', 'admin-workflows', 'noop'],
    queryFn: () => getWorkflow(id as string),
    enabled: !!id,
  });
}

export function useThresholds() {
  return useQuery({
    queryKey: ADMIN_WORKFLOW_KEYS.thresholds(),
    queryFn: listThresholds,
  });
}

export function useCriarWorkflow() {
  const invalidar = useInvalidarWorkflows();
  return useMutation({
    mutationFn: (payload: CreateWorkflowPayload) => createWorkflow(payload),
    onSuccess: () => invalidar(),
  });
}

export function useAtualizarWorkflow() {
  const invalidar = useInvalidarWorkflows();
  return useMutation({
    mutationFn: (input: { id: string; payload: UpdateWorkflowPayload }) =>
      updateWorkflow(input.id, input.payload),
    onSuccess: () => invalidar(),
  });
}

export function useRemoverWorkflow() {
  const invalidar = useInvalidarWorkflows();
  return useMutation({
    mutationFn: (id: string) => deleteWorkflow(id),
    onSuccess: () => invalidar(),
  });
}

export function useAdicionarWorkflowStep(workflowId: string) {
  const invalidar = useInvalidarWorkflows();
  return useMutation({
    mutationFn: (payload: CreateWorkflowStepPayload) => addWorkflowStep(workflowId, payload),
    onSuccess: () => invalidar(),
  });
}

export function useAtualizarWorkflowStep(workflowId: string) {
  const invalidar = useInvalidarWorkflows();
  return useMutation({
    mutationFn: (input: { stepId: string; payload: UpdateWorkflowStepPayload }) =>
      updateWorkflowStep(workflowId, input.stepId, input.payload),
    onSuccess: () => invalidar(),
  });
}

export function useRemoverWorkflowStep(workflowId: string) {
  const invalidar = useInvalidarWorkflows();
  return useMutation({
    mutationFn: (stepId: string) => removeWorkflowStep(workflowId, stepId),
    onSuccess: () => invalidar(),
  });
}

export function useUpsertThreshold() {
  const invalidar = useInvalidarWorkflows();
  return useMutation({
    mutationFn: (input: { chave: string; payload: UpsertThresholdPayload }) =>
      upsertThreshold(input.chave, input.payload),
    onSuccess: () => invalidar(),
  });
}
